/**
 * Facet Landing Pages
 * Minimal gateway pages for each of the four facets
 */

import GroupGlimpse from '@/components/group/GroupGlimpse';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Scale, AlertTriangle, ShieldCheck, FileSearch, Building2, Landmark,
  Users, Mail, Bell, MessageCircle, Calendar, Package, Key,
  ClipboardCheck, Briefcase, ShoppingCart, ChevronRight, Info,
  Shield, TrendingUp, Home
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════
// FACET 1: RECOURSE & CLAIMS
// ═══════════════════════════════════════════════════════════════════
export function RecourseLanding({ onNavigate }) {
  const sections = [
    {
      id: 'legal-services',
      icon: Scale,
      title: 'S.18 Interest Claims',
      description: 'Understand your rights when possession is delayed. Claim interest under RERA Section 18 with collective support.',
      badge: 'Live',
      badgeColor: 'bg-green-100 text-green-700'
    },
    {
      id: 'subvention-claims',
      icon: AlertTriangle,
      title: 'Subvention & Pre-EMI Claims',
      description: 'Developer promised to pay Pre-EMI but stopped? Stuck with EMI + rent? Recourse against developer AND bank.',
      badge: 'New',
      badgeColor: 'bg-red-100 text-red-700'
    },
    {
      id: 'quality-issues',
      icon: AlertTriangle,
      title: 'Quality Issues',
      description: 'Report deviations from promised specifications. Document and track quality concerns with your flat.',
      badge: 'Coming Soon',
      badgeColor: 'bg-amber-100 text-amber-700'
    }
  ];

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="recourse-landing">
      {/* Header */}
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="p-2 sm:p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg shrink-0">
          <Scale className="h-6 w-6 sm:h-8 sm:w-8 text-amber-600" />
        </div>
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
            Recourse & Claims
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            When delivery is delayed or compromised, know your options and rights.
          </p>
        </div>
      </div>

      {/* Context */}
      <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-900/10">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800 dark:text-amber-200">
              <strong>FBC's Approach:</strong> We facilitate constructive resolution between buyers and developers. 
              When amicable solutions aren't possible, we help you understand and exercise your legal rights 
              through collective representation.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section Cards */}
      <div className="grid gap-4">
        {sections.map(section => (
          <Card 
            key={section.id}
            className="cursor-pointer hover:border-amber-300 transition-colors"
            onClick={() => onNavigate(section.id)}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                  <section.icon className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">{section.title}</h3>
                    <Badge className={section.badgeColor}>{section.badge}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{section.description}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FACET 2: RISK ASSESSMENTS
// ═══════════════════════════════════════════════════════════════════
export function RiskLanding({ onNavigate, isVerified, focusPillar }) {
  // The pillars - framed as helpful buyer education
  const assurancePillars = [
    {
      id: 'financial-assurance',
      icon: Landmark,
      title: 'Financial Health Visibility',
      buyerQuestion: 'What is the financial standing of this project?',
      whatToAsk: [
        'Credit rating of the developer (if publicly available)',
        'Project funding structure — bank funded or self-funded',
        'RERA escrow account compliance status',
        'Names of lending institutions associated with the project',
        'Corpus rate (₹ per RERA carpet sq ft), basis, and the developer\'s written transfer plan to the society'
      ],
      whyItMatters: 'Financial visibility helps you assess project completion timelines with greater confidence. The Corpus, in particular, is the buyers\' money and must flow intact to the society on member admission.',
      industryStandard: 'Banks review developer financials before approving your home loan. You can request similar visibility.',
      status: 'advocacy',
      liveLink: { label: 'See actual Form 3 escrow filings for tracked projects', section: 'browse-projects' }
    },
    {
      id: 'approval-assurance',
      icon: Building2,
      title: 'Approval & Regulatory Status',
      buyerQuestion: 'What is the current approval status of this project?',
      whatToAsk: [
        'Commencement Certificate (CC) — available on municipal portals',
        'Environmental clearance status (if applicable for the location)',
        'Fire NOC — provisional or final',
        'Approved building plan for your wing/tower'
      ],
      whyItMatters: 'Understanding approval status helps you plan your possession timeline realistically.',
      industryStandard: 'Home loan lenders typically review project approvals. You can request the same information.',
      status: 'advocacy'
    },
    {
      id: 'title-assurance',
      icon: FileSearch,
      title: 'Title & Legal Clarity',
      buyerQuestion: 'What is the title status of this property?',
      whatToAsk: [
        'Title search report (you may commission your own lawyer)',
        'Encumbrance certificate for the land parcel',
        'Any pending litigation disclosed on RERA',
        'Title insurance options (where available)'
      ],
      whyItMatters: 'Clear title documentation provides peace of mind for your long-term ownership.',
      industryStandard: 'Banks conduct title verification before disbursing your home loan. You can seek similar clarity.',
      status: 'advocacy'
    },
    {
      id: 'loan-subvention-safety',
      icon: Landmark,
      title: 'Loan & Subvention Safety',
      buyerQuestion: 'Is the subvention scheme safe? What clauses protect me?',
      whatToAsk: [
        'Tripartite agreement (Buyer-Developer-Bank) with clear liability clauses',
        'Written confirmation that Pre-EMI is developer\'s responsibility till possession',
        'Construction-linked disbursement schedule (not upfront to developer)',
        'Clause protecting your CIBIL score if developer defaults on Pre-EMI',
        'Bank\'s confirmation of milestone-based disbursement verification process'
      ],
      whyItMatters: 'In subvention schemes, if the developer stops paying Pre-EMI after delays, the bank comes after YOU. Thousands of buyers are trapped paying EMI + rent with no flat in sight.',
      industryStandard: 'RBI guidelines require banks to verify construction milestones before disbursement. Insist on this protection.',
      status: 'advocacy'
    }
  ];

  // When opened via a sub-section route, show ONLY that pillar (not the full checklist).
  // When opened via the parent Buyer's Checklist route, show all pillars.
  const isFocused = !!focusPillar;
  const visiblePillars = isFocused
    ? assurancePillars.filter(p => p.id === focusPillar)
    : assurancePillars;
  const focusedPillar = isFocused ? visiblePillars[0] : null;

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="risk-landing">
      {/* Header - Helpful framing */}
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="p-2 sm:p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg shrink-0">
          <ShieldCheck className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
        </div>
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
            {focusedPillar ? focusedPillar.title : "Buyer's Information Checklist"}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            {focusedPillar
              ? 'A focused area of the Buyer\u2019s Checklist.'
              : 'Key information that helps you make an informed decision.'}
          </p>
          {focusedPillar && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-2 -ml-2 text-blue-600 hover:text-blue-700"
              onClick={() => onNavigate?.('risk-landing')}
              data-testid="back-to-full-checklist-btn"
            >
              <ChevronRight className="h-4 w-4 rotate-180 mr-1" />
              See full Buyer's Checklist
            </Button>
          )}
        </div>
      </div>

      {/* Strategic Message — only on full checklist view */}
      {!isFocused && (
      <Card className="border-blue-200 bg-blue-50/50 dark:bg-blue-900/10">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Be Informed:</strong> When making one of life's largest purchases, 
              having visibility into key project information can help you plan better. 
              Here are areas where you may seek clarity from your developer.
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* The pillar(s) — focused view shows only the relevant one */}
      <div className="grid gap-4">
        {visiblePillars.map(pillar => (
          <Card
            key={pillar.id}
            className="overflow-hidden"
            data-testid={`pillar-${pillar.id}`}
          >
            <CardContent className="p-0">
              {/* Header */}
              <div className="p-4 bg-gradient-to-r from-blue-50 to-transparent dark:from-blue-900/20 border-b">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                    <pillar.icon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{pillar.title}</h3>
                    <p className="text-primary font-medium mt-1">"{pillar.buyerQuestion}"</p>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-4 space-y-4">
                {/* What to Ask */}
                <div>
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                    Ask Your Developer For:
                  </h4>
                  <ul className="space-y-1">
                    {pillar.whatToAsk.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <ChevronRight className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Why it matters */}
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-800">
                  <p className="text-sm text-red-800 dark:text-red-200">
                    <strong>Why it matters:</strong> {pillar.whyItMatters}
                  </p>
                </div>
                
                {/* Industry standard callout */}
                <div className="text-sm text-muted-foreground italic">
                  💡 {pillar.industryStandard}
                </div>

                {/* Live data bridge — where FBC has real filings for this pillar */}
                {pillar.liveLink && (
                  <Button
                    variant="outline" size="sm"
                    className="text-blue-700 border-blue-300 hover:bg-blue-50"
                    onClick={() => onNavigate?.(pillar.liveLink.section)}
                    data-testid={`pillar-live-${pillar.id}`}
                  >
                    <Landmark className="h-3.5 w-3.5 mr-1.5" />
                    {pillar.liveLink.label}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Bottom CTAs only on the full Checklist view, not single-pillar deep-dives */}
      {!isFocused && (
        <>
        {/* Call to Action - Share with community */}
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="pt-4">
            <div className="text-center space-y-3">
              <h3 className="font-semibold">Share This Checklist</h3>
              <p className="text-sm text-muted-foreground">
                Know someone planning to buy a flat? Share this information checklist with them.
              </p>
              <div className="flex justify-center gap-3">
                <Button variant="outline" size="sm" onClick={() => {
                  const text = "Useful checklist for flat buyers - covers financial, approval and title information to review before booking. Check it out at FBC.";
                  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                }}>
                  Share on WhatsApp
                </Button>
                <Button variant="outline" size="sm" onClick={() => onNavigate('community-forum')}>
                  Discuss in Community
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* For Developers */}
        <Card className="border-dashed">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-sm">Are you a Developer?</h4>
                <p className="text-xs text-muted-foreground">
                  Learn how transparency can help you connect better with informed buyers.
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => onNavigate('developer-landing')}>
                Learn More →
              </Button>
            </div>
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FACET 3: BUYERS' GROUP
// ═══════════════════════════════════════════════════════════════════
export function CommunityLanding({ onNavigate, isVerified }) {
  const sections = [
    {
      id: 'us-directory',
      icon: Users,
      title: 'Directory',
      description: 'Connect with fellow buyers from your project. See who\'s in your building/wing.',
    },
    {
      id: 'us-correspondence',
      icon: Mail,
      title: 'Correspondence',
      description: 'Collective communication with developer and authorities.',
    },
    {
      id: 'us-notices',
      icon: Bell,
      title: 'Notices',
      description: 'Important announcements and updates for your project.',
    },
    {
      id: 'us-threads',
      icon: MessageCircle,
      title: 'Threads',
      description: 'Discussion forums for project-specific topics.',
    },
    {
      id: 'us-meetings',
      icon: Calendar,
      title: 'Meetings',
      description: 'Schedule and track buyer meetings and AGMs.',
    },
    {
      id: 'property-marketplace',
      icon: Home,
      title: 'Classifieds',
      description: 'Buy, sell, rent or lease flats within your community. Verified owner listings only.',
    }
  ];

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="community-landing">
      {/* Header */}
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="p-2 sm:p-3 bg-green-100 dark:bg-green-900/30 rounded-lg shrink-0">
          <Users className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
        </div>
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
            Buyers' Group
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Strength in community. Connect with fellow buyers from your project.
          </p>
        </div>
      </div>

      {/* Context */}
      <Card className="border-green-200 bg-green-50/50 dark:bg-green-900/10">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
            <div className="text-sm text-green-800 dark:text-green-200">
              <strong>Community power:</strong> Informed, organized buyers have a stronger voice. 
              Share experiences, stay updated on project developments, and coordinate on common concerns.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section Cards */}
      <div className="grid sm:grid-cols-2 gap-4">
        {sections.map(section => (
          <Card 
            key={section.id}
            className={`cursor-pointer transition-colors ${isVerified ? 'hover:border-green-300' : 'opacity-60'}`}
            onClick={() => isVerified && onNavigate(section.id)}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <section.icon className="h-4 w-4 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm">{section.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1">{section.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {!isVerified && <GroupGlimpse />}

      {!isVerified && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="pt-4 text-center">
            <p className="text-amber-800 mb-3">Verify your ownership to join the Buyers' Group</p>
            <Button onClick={() => onNavigate('verify-ownership')}>Verify Ownership</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FACET 5: DEVELOPER UPDATES
// ═══════════════════════════════════════════════════════════════════
export function DeveloperLanding({ onNavigate }) {
  const sections = [
    {
      id: 'project-dev-info',
      icon: Building2,
      title: 'Project Updates',
      description: 'Published information, construction progress, milestones, and timeline updates from developers.',
    },
  ];

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="developer-landing">
      {/* Header */}
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="p-2 sm:p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg shrink-0">
          <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-indigo-600" />
        </div>
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
            Developer Updates
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Transparency from builders. Stay informed about your project's progress.
          </p>
        </div>
      </div>

      {/* Context */}
      <Card className="border-indigo-200 bg-indigo-50/50 dark:bg-indigo-900/10">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-indigo-600 shrink-0 mt-0.5" />
            <div className="text-sm text-indigo-800 dark:text-indigo-200">
              <strong>FBC Trust Bridge:</strong> We encourage developers to proactively share updates with buyers. 
              Transparency builds trust and helps create constructive dialogue throughout your property journey.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section Cards */}
      <div className="grid gap-4">
        {sections.map(section => (
          <Card 
            key={section.id}
            className="cursor-pointer hover:border-indigo-300 transition-colors"
            onClick={() => onNavigate(section.id)}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                  <section.icon className="h-5 w-5 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{section.title}</h3>
                  <p className="text-sm text-muted-foreground">{section.description}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Developer Registration CTA */}
      <Card className="border-indigo-300 bg-gradient-to-r from-indigo-50 to-transparent dark:from-indigo-900/20">
        <CardContent className="p-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold text-indigo-800 dark:text-indigo-200">Are you a Developer?</h3>
              <p className="text-sm text-muted-foreground mt-1">Claim your project on FBC. Post updates, engage with your buyers, and build trust through transparency.</p>
            </div>
            <Button onClick={() => onNavigate('developer-register')} className="bg-indigo-600 hover:bg-indigo-700 shrink-0" data-testid="developer-register-cta">
              Register as Developer
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FACET 4: SERVICES & PROCUREMENT
// ═══════════════════════════════════════════════════════════════════
export function ServicesLanding({ onNavigate }) {
  const sections = [
    {
      id: 'pre-possession',
      icon: Key,
      title: 'Pre-Possession Services',
      description: 'Snagging inspections, pre-handover checks, and fit-out planning.',
      category: 'Possession Support'
    },
    {
      id: 'post-possession',
      icon: ClipboardCheck,
      title: 'Post-Possession Services',
      description: 'Issue tracking, maintenance coordination, and society formation guidance.',
      category: 'Possession Support'
    },
    {
      id: 'service-providers',
      icon: Briefcase,
      title: 'Service Providers',
      description: 'Verified professionals for interiors, fit-outs, legal, and more.',
      category: 'Procurement'
    },
    {
      id: 'group-buying',
      icon: ShoppingCart,
      title: 'Group Buying',
      description: 'Leverage collective buying power for better rates on appliances, furniture, and services.',
      category: 'Procurement',
      badge: 'Coming Soon',
      badgeColor: 'bg-teal-100 text-teal-700'
    },
    {
      id: 'property-marketplace',
      icon: Home,
      title: 'Buy / Rent / Lease',
      description: 'Property classifieds from verified owners. Resale, rental, and wanted listings.',
      category: 'Classifieds',
      badge: 'Live',
      badgeColor: 'bg-green-100 text-green-700'
    }
  ];

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="services-landing">
      {/* Header */}
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="p-2 sm:p-3 bg-teal-100 dark:bg-teal-900/30 rounded-lg shrink-0">
          <Package className="h-6 w-6 sm:h-8 sm:w-8 text-teal-600" />
        </div>
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>
            Services & Procurement
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Better rates through collective leverage. Trusted vendors for your home journey.
          </p>
        </div>
      </div>

      {/* Context */}
      <Card className="border-teal-200 bg-teal-50/50 dark:bg-teal-900/10">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" />
            <div className="text-sm text-teal-800 dark:text-teal-200">
              <strong>Collective advantage:</strong> As a group of buyers, FBC negotiates better rates 
              with service providers and vendors. Access quality services at preferential pricing.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section Cards */}
      <div className="grid gap-4">
        {sections.map(section => (
          <Card 
            key={section.id}
            className="cursor-pointer hover:border-teal-300 transition-colors"
            onClick={() => onNavigate(section.id)}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                  <section.icon className="h-5 w-5 text-teal-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">{section.title}</h3>
                    {section.badge && (
                      <Badge className={section.badgeColor}>{section.badge}</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{section.description}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
